import styled, { css } from 'styled-components';
import Link from 'next/link';
import { FlexCenterAligned, FlexibleDiv } from '../../styled/common';
import { Breakpoints } from '../../styled/constants';
import { FlexibleHeaderProps } from '.';
import { CustomLink } from '../CustomLink';
import { PlaceholderBlock } from '../../styled/placeholders.styled';
import {
  getDefaultMenuItemColor,
  getHoverMenuItemColor,
  getSelectedMenuItemStyles
} from '../../styled/theme/helpers';

export const FlexedHeader = styled(FlexCenterAligned).attrs({ as: 'header' })<
  Pick<FlexibleHeaderProps, 'isFixedHeader'>
>`
  background-color: ${({ theme }) => theme.colors.background.primary};
  ${({ isFixedHeader }) =>
    isFixedHeader &&
    css`
      position: sticky;
      width: 100%;
      height: 60px;
      top: 0;
      left: 0;
      z-index: 95;

      ~ .placeholder-st {
        margin-top: 25px;
      }
    `};
`;

export const InitialFlexedHeader = styled(FlexedHeader)`
  @media screen and (max-width: ${({ theme }) => theme.tabletBreakpoint}) {
    height: 60px;
  }

  .link {
    @media screen and (max-width: ${Breakpoints.tablet}px) {
      display: none;
    }
  }
`;

export const ButtonPlaceholder = styled(PlaceholderBlock)`
  height: 20px;
  width: 20px;
  margin-right: 20px;
  border-radius: 5px;
  align-self: center;
`;

export const InitialLogo = styled(CustomLink)`
  margin-right: 25px;
  display: flex;
  height: 24px;
  width: 118px;
  justify-content: space-between;

  svg path {
    fill: ${({ theme }) => theme.colors.systemColors.brand};
  }
`;

export const Wrapper = styled.div<{ $hasAuthOrMoreHash: boolean }>`
  display: flex;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  position: sticky;
  z-index: ${({ $hasAuthOrMoreHash }) => ($hasAuthOrMoreHash ? '10' : '70001')};
  top: 0;
  align-items: center;
  padding: 12px 15px 15px;

  @media screen and (min-width: ${({ theme }) =>
      theme.tabletBreakpoint}) and (max-width: ${({ theme }) =>
      theme.desktopBreakpoint}) {
    padding: 12px 40px 15px;
  }

  @media screen and (min-width: ${({ theme }) => theme.desktopBreakpoint}) {
    margin: 0 80px;
    padding: 12px 0 15px;
    max-width: ${({ theme }) => theme.desktopBreakpoint};
  }
`;

export const ButtonsGroupContainer = styled(FlexibleDiv)`
  align-items: center;
  gap: 14px;
  margin-left: auto;

  .small-add-link {
    @media screen and (min-width: ${({ theme }) => theme.desktopBreakpoint}) {
      display: none;
    }
  }
`;

export const CreateItemIconLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  text-align: center;
  font-size: 15px;
  font-weight: 500;
  padding: 10px 15px;
  white-space: nowrap;
  text-decoration: none;
  color: #fff;
  background-color: #3db460;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  height: 44px;
  border-radius: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #269547;
  }
`;

export const BookmarkIconLink = styled.a`
  display: flex;
  align-items: center;
  width: 30px;
  height: 30px;

  svg path {
    stroke: ${({ theme }) => theme.colors.text.primary};
  }

  &:hover > svg path {
    stroke: ${({ theme }) => theme.colors.systemColors.blue};
  }
`;

export const MenuItemsContainer = styled(FlexibleDiv)`
  gap: 10px;
  align-items: center;
`;

const shouldForwardProp = (prop: string) => !['isCurrentPage'].includes(prop);

export const MenuItem = styled(CustomLink).withConfig({
  shouldForwardProp
})<{ isCurrentPage: boolean }>`
  padding: 10px 5px;
  font-size: 15px;
  color: ${({ theme }) => getDefaultMenuItemColor(theme)};
  text-decoration: none;
  transition: color 0.2s ease-in-out;
  letter-spacing: 0.05px;

  ${({ isCurrentPage, theme }) =>
    isCurrentPage && getSelectedMenuItemStyles(theme)}

  &:hover {
    color: ${({ theme }) => getHoverMenuItemColor(theme)};
  }
`;

export const DesktopMenuItem = styled(MenuItem)`
  @media screen and (max-width: ${Breakpoints.smallDesktop}px) {
    display: none;
  }
`;

export const LargeCreateItemLink = styled(CreateItemIconLink)`
  @media screen and (max-width: ${Breakpoints.desktop}px) {
    display: none;
  }
`;

export const WebViewButton = styled.button`
  position: absolute;
  right: 0;
  width: 45px;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  padding: 0;

  i {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  svg {
    display: block;
    flex: 1;
  }
`;

export const PashaMenuItem = styled(DesktopMenuItem)<{
  isCurrentPage: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 5px;

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    fill: currentColor;
    transition: fill 0.2s ease-in-out;
  }

  color: ${({ theme }) => getDefaultMenuItemColor(theme)};

  &:hover {
    color: ${({ theme }) => getHoverMenuItemColor(theme)};
  }

  ${({ isCurrentPage, theme }) =>
    isCurrentPage && getSelectedMenuItemStyles(theme)};

  @media screen and (max-width: ${Breakpoints.PashaSmallDesktop}px) {
    display: none;
  }
`;
